import React, { useMemo, useState } from "react";
import { Card } from "reactstrap";

import { Row, Col, Button, RSelect } from "../Component";
import MultiLanguageInput from "./MultiLanguageInput";
import MultiLanguageListInput from "./MultiLanguageListInput";

import {
  VACANCY_TYPES,
  VACANCY_TYPES_READABLE,
} from "../../utilities/constants";

const FormSection = ({ title, subtitle, children }) => (
  <Card className="card-bordered card-inner shadow-sm mb-4">
    <div className="mb-3">
      <h6 className="mb-1">{title}</h6>
      {subtitle && <div className="form-note m-0">{subtitle}</div>}
    </div>
    {children}
  </Card>
);

const VacancyForm = ({ initialValues, onSubmit, onCancel, isLoading }) => {
  const [form, setForm] = useState(initialValues);

  // Saqlash uchun eng kam talab: lavozim nomi uchala tilda va vacancy type.
  const isJobTitleFilled = Object.values(form.jobTitle ?? {}).every(
    (text) => String(text ?? "").trim().length > 0
  );
  const canSubmit = Boolean(form.vacancyType) && isJobTitleFilled;

  const typeOptions = useMemo(
    () =>
      Object.values(VACANCY_TYPES).map((value) => ({
        value,
        label: VACANCY_TYPES_READABLE[value],
      })),
    []
  );

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setSectionField = (section, field, value) =>
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!canSubmit) return;

    onSubmit(form);
  };

  const detailFields = [
    { field: "jobDescription", label: "Job description" },
    { field: "subDescription", label: "Sub description" },
    { field: "reportsTo", label: "Reports to" },
    { field: "term", label: "Term" },
    {
      field: "deadline",
      label: "Deadline",
      hint: "Matn sifatida saqlanadi, masalan 07.11.2026",
    },
  ];

  const listFields = [
    { field: "arguments", label: "What we offer", addButtonTitle: "Add offer" },
    {
      field: "qualification",
      label: "Qualification",
      addButtonTitle: "Add qualification",
    },
    {
      field: "experience",
      label: "Experience",
      addButtonTitle: "Add experience",
    },
    { field: "skills", label: "Skills", addButtonTitle: "Add skill" },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <FormSection
        title="Basic Information"
        subtitle="Lavozim nomi uchala tilda to'ldirilishi kerak"
      >
        <MultiLanguageInput
          label="Job title"
          value={form.jobTitle}
          onChange={(value) => setField("jobTitle", value)}
        />

        <Row>
          <Col md="6" className="mb-3 mb-md-0">
            <div className="border rounded p-3 bg-white h-100">
              <label className="form-label">Vacancy type *</label>
              <RSelect
                options={typeOptions}
                value={
                  typeOptions.find(
                    (option) => option.value === form.vacancyType
                  ) ?? null
                }
                onChange={(option) =>
                  setField("vacancyType", option?.value ?? "")
                }
                placeholder="Select vacancy type"
              />
            </div>
          </Col>
          <Col md="6">
            <div className="border rounded p-3 bg-white h-100">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">Display order</label>
                <span className="form-note m-0">Kichik raqam oldinroq</span>
              </div>
              <input
                type="number"
                className="form-control"
                placeholder="1"
                value={form.orderIndex}
                onChange={(e) => setField("orderIndex", e.target.value)}
              />
            </div>
          </Col>
        </Row>

        <div className="border rounded p-3 bg-white mt-3 d-flex align-items-center justify-content-between">
          <label className="form-label mb-0">Active</label>
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="vacancyIsActive"
              checked={form.isActive === true}
              onChange={(e) => setField("isActive", e.target.checked)}
            />
            <label
              className="custom-control-label"
              htmlFor="vacancyIsActive"
            ></label>
          </div>
        </div>
      </FormSection>

      <FormSection title="Details">
        {detailFields.map((item) => (
          <MultiLanguageInput
            key={item.field}
            label={item.label}
            hint={item.hint}
            value={form[item.field]}
            onChange={(value) => setField(item.field, value)}
            isRichText
          />
        ))}
      </FormSection>

      <FormSection
        title="About University"
        subtitle="Vakansiya sahifasida universitet haqidagi blok"
      >
        <MultiLanguageInput
          label="Title"
          value={form.aboutUniversity.title}
          onChange={(value) => setSectionField("aboutUniversity", "title", value)}
          isRichText
        />
        <MultiLanguageListInput
          label="Paragraphs"
          items={form.aboutUniversity.description}
          onChange={(items) =>
            setSectionField("aboutUniversity", "description", items)
          }
          addButtonTitle="Add paragraph"
        />
      </FormSection>

      <FormSection
        title="Disciplines"
        subtitle="Ixtiyoriy — to'ldirilmasa yuborilmaydi"
      >
        <MultiLanguageInput
          label="Title"
          value={form.disciplines.title}
          onChange={(value) => setSectionField("disciplines", "title", value)}
          isRichText
        />
        <MultiLanguageListInput
          label="Items"
          items={form.disciplines.arguments}
          onChange={(items) =>
            setSectionField("disciplines", "arguments", items)
          }
          addButtonTitle="Add discipline"
        />
      </FormSection>

      <FormSection
        title="Application Process"
        subtitle="Ixtiyoriy — to'ldirilmasa yuborilmaydi"
      >
        <MultiLanguageInput
          label="Title"
          value={form.applicationProcess.title}
          onChange={(value) =>
            setSectionField("applicationProcess", "title", value)
          }
          isRichText
        />
        <MultiLanguageListInput
          label="Items"
          items={form.applicationProcess.arguments}
          onChange={(items) =>
            setSectionField("applicationProcess", "arguments", items)
          }
          addButtonTitle="Add step"
        />
      </FormSection>

      <FormSection
        title="Requirements"
        subtitle="Bo'sh qoldirilgan qatorlar saqlanmaydi"
      >
        {listFields.map((item) => (
          <MultiLanguageListInput
            key={item.field}
            label={item.label}
            items={form[item.field]}
            onChange={(items) => setField(item.field, items)}
            addButtonTitle={item.addButtonTitle}
          />
        ))}
      </FormSection>

      <div className="d-flex align-items-center justify-content-end pb-4">
        {!canSubmit && (
          <span className="form-note mr-3">
            Saqlash uchun lavozim nomi uchala tilda va vacancy type
            to'ldirilishi kerak
          </span>
        )}
        <Button
          color="white border-light"
          className="mr-2"
          onClick={(e) => {
            e.preventDefault();
            onCancel();
          }}
          title="Cancel"
        />
        <Button
          color="primary"
          type="submit"
          iconName="save"
          title="Save"
          disabled={!canSubmit}
          isLoading={isLoading}
        />
      </div>
    </form>
  );
};

export default VacancyForm;
